import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { ExportButtons } from "@/components/ExportButtons";
import { DateFilters, filterParams, previousMonthFilters } from "@/components/DateFilters";
import { API, useAuth, isAdmin } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";
import { GSTR2Drill } from "@/components/GSTR2Drill";
import { GSTR2DocSheet } from "@/components/GSTR2DocSheet";
import { GSTR2Rollup } from "@/components/GSTR2Rollup";
import { GSTR2TaxCodes, CategoryChip } from "@/components/GSTR2TaxCodes";
import { GSTR2TrialBalance } from "@/components/GSTR2TrialBalance";
import { GSTR2GLHistory } from "@/components/GSTR2GLHistory";
import { GSTR2BankCharges } from "@/components/GSTR2BankCharges";
import { GSTR2SapReconcile } from "@/components/GSTR2SapReconcile";

const Stat = ({ label, value, testId, onClick }) => (
  <Card className={`border-slate-200 shadow-sm ${onClick ? "cursor-pointer hover:border-sky-300 hover:shadow-md transition-[box-shadow,border-color]" : ""}`} onClick={onClick} role={onClick ? "button" : undefined} data-testid={onClick ? `${testId}-tile` : undefined} title={onClick ? "Click to see the lines behind this figure" : undefined}>
    <CardContent className="p-4">
      <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-1">{label}</p>
      <p className="text-xl font-black tracking-tight text-slate-900" data-testid={testId}>{value}</p>
    </CardContent>
  </Card>
);

export default function GSTR2() {
  const { user } = useAuth();
  const [vendor, setVendor] = useState("");
  const [filters, setFilters] = useState(() => previousMonthFilters());
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);
  const [docRow, setDocRow] = useState(null);
  const PAGE = 300;
  const drillParams = useMemo(() => ({ ...(vendor ? { vendor } : {}), ...filterParams(filters) }), [vendor, filters]);
  const tbParams = useMemo(() => filterParams(filters), [filters]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/reports/gstr2`, { params: { ...(vendor ? { vendor } : {}), ...filterParams(filters), limit: PAGE, offset: page * PAGE } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [vendor, filters, page]);
  useEffect(() => { setPage(0); }, [vendor, filters]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    window.addEventListener("sap-synced", load);
    return () => { clearTimeout(t); window.removeEventListener("sap-synced", load); };
  }, [load]);

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading GSTR-2 report...</div>;
  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="gstr2-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">GSTR-2 Report</h1>
          <p className="text-sm text-slate-500 mt-1">Inward supplies / purchase register with input tax credit — India GST</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ExportButtons report="gstr2" endpoint="/reports/gstr2/export" params={drillParams} formats={["csv", "xlsx"]} />
          <span className="text-[10px] text-slate-400" data-testid="gstr2-export-note">Excel = Register + Bank Charges + By HSN + HSN Lines + By Tax Code + By Vendor (current filters)</span>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="gstr2-filters">
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="relative w-72 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="gstr2-vendor-search" placeholder="Filter by vendor name or supplier code..." value={vendor} onChange={(e) => setVendor(e.target.value)} className="pl-9 h-9" />
          </div>
          <DateFilters filters={filters} setFilters={setFilters} testPrefix="gstr2" />
          <span className="text-[11px] text-slate-400 ml-auto">Vendor + date filters apply to every tile, card, drill-down and the register below</span>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 fade-in-up">
        <Stat label="Taxable Value" value={inrShort(s.taxable_value)} testId="gstr2-taxable" onClick={() => setDrill({ metric: "taxable" })} />
        <Stat label="IGST" value={inrShort(s.igst)} testId="gstr2-igst" onClick={() => setDrill({ metric: "igst" })} />
        <Stat label="CGST" value={inrShort(s.cgst)} testId="gstr2-cgst" onClick={() => setDrill({ metric: "cgst" })} />
        <Stat label="SGST" value={inrShort(s.sgst)} testId="gstr2-sgst" onClick={() => setDrill({ metric: "sgst" })} />
        <Stat label="Total Tax" value={inrShort(s.total_tax)} testId="gstr2-total-tax" onClick={() => setDrill({ metric: "total_tax" })} />
        <Stat label="Eligible ITC" value={inrShort(s.itc_eligible_tax)} testId="gstr2-itc" onClick={() => setDrill({ metric: "itc" })} />
      </div>
      <GSTR2Drill drill={drill} params={drillParams} onClose={() => setDrill(null)} onOpenDoc={setDocRow} />
      <GSTR2DocSheet row={docRow} onClose={() => setDocRow(null)} />
      {data.gl_tie && (
        <Card className="border-emerald-200 bg-emerald-50/40 shadow-sm fade-in-up" data-testid="gstr2-gl-tie">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-emerald-900">Ties to the Input-GST general ledger (by posting date)</p>
                <p className="text-[11px] text-emerald-800/80 max-w-3xl">{data.gl_tie.note}</p>
              </div>
              <div className="text-xs text-emerald-900 flex flex-wrap gap-3">
                <button type="button" onClick={() => setDrill({ metric: "ineligible" })} data-testid="gstr2-ineligible-btn" className="hover:underline">Ineligible / blocked ITC: <b data-testid="gstr2-ineligible">{inrShort(s.ineligible_tax)}</b></button>
                <button type="button" onClick={() => setDrill({ metric: "rcm" })} data-testid="gstr2-rcm-btn" className="hover:underline" title="Net movement on the RCM accounts (credit = liability)">RCM liability (net): <b data-testid="gstr2-rcm">{inrShort(s.rcm_tax)}</b></button>
                <span>{data.gl_tie.register_lines} GL-sourced lines</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2">
              {Object.entries(data.gl_tie.gl_movement).map(([gl, v]) => (
                <button type="button" key={gl} onClick={() => setDrill({ metric: "gl", value: gl })} className="rounded-md border border-emerald-200 bg-white px-3 py-2 text-left hover:border-emerald-400 hover:bg-emerald-50 transition-colors" data-testid={`gstr2-gl-${gl.replace(/\s+/g, "-").toLowerCase()}`} title="Lines posted to this GL account">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{gl}</div>
                  <div className="text-sm font-semibold tabular-nums">{inr(v)}</div>
                </button>
              ))}
            </div>
            {data.gl_tie.by_type?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="gstr2-gl-by-type">
                {data.gl_tie.by_type.map((b) => (
                  <button type="button" key={b.type} onClick={() => setDrill({ metric: "type", value: b.type })} data-testid={`gstr2-type-chip-${b.type.replace(/[^\w]+/g, "-").toLowerCase()}`} className="rounded-full border border-emerald-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-700 hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                    {b.type}: {b.count} · {inrShort(b.total_tax)}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <GSTR2BankCharges bank={data.bank_charges} onDrill={setDrill} />

      {data.rate_summary && data.rate_summary.length > 0 && (
        <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="gstr2-rate-summary">
          <CardContent className="p-4">
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-3">Rate-wise Summary (GST Slabs)</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200">
                    {["GST Rate", "Invoices", "Taxable", "IGST", "CGST", "SGST", "Total Tax"].map((h) => (
                      <TableHead key={h} className={`text-xs uppercase tracking-wider text-slate-500 ${h !== "GST Rate" ? "text-right" : ""}`}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rate_summary.map((r) => (
                    <TableRow key={r.rate} className="border-slate-100 hover:bg-sky-50/60 cursor-pointer" onClick={() => setDrill({ metric: "rate", value: String(r.rate) })} data-testid={`gstr2-rate-row-${r.rate}`} title="Lines at this rate">
                      <TableCell><Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">{r.rate}%</Badge></TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{r.count}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{inr(r.taxable_value)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{r.igst ? inr(r.igst) : "—"}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{r.cgst ? inr(r.cgst) : "—"}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{r.sgst ? inr(r.sgst) : "—"}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-slate-900">{inr(r.total_tax)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <GSTR2SapReconcile onOpenDoc={setDocRow} />
      <GSTR2TrialBalance params={tbParams} onOpenDoc={setDocRow} onRegisterRebuilt={load} />
      <GSTR2GLHistory />
      <GSTR2TaxCodes canEdit={isAdmin(user)} onDrill={setDrill} onChanged={load} />
      <GSTR2Rollup params={drillParams} onDrill={setDrill} />

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardContent className="p-4 space-y-4">
          <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Purchase register</p>
          <div className="overflow-x-auto">
            <Table data-testid="gstr2-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Invoice", "Supplier Code", "Vendor", "GSTIN", "Date", "Place of Supply", "Type", "Rate", "Taxable", "IGST", "CGST", "SGST", "Invoice Value", "ITC"].map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invoices.map((r) => (
                  <TableRow key={r.id} className="border-b border-slate-200 hover:bg-sky-50/60 cursor-pointer" onClick={() => setDocRow(r)} data-testid={`gstr2-row-${r.id}`} title="Open the SAP accounting document">
                    <TableCell className="text-sm font-medium text-slate-800">{r.invoice_no}{r.accounting_document ? <span className="block text-[10px] font-mono text-slate-400">{r.accounting_document}/{r.fiscal_year}</span> : null}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`gstr2-vendor-code-${r.invoice_no}`}>{r.vendor_code ? <Link to={`/vendor/${r.vendor_code}`} onClick={(e) => e.stopPropagation()} className="text-sky-700 hover:underline" title="Vendor 360° profile">{r.vendor_code}</Link> : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700 whitespace-nowrap">{r.vendor_name}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{r.gstin}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{r.invoice_date}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="text-sm font-medium text-slate-700">{r.place_of_supply || "—"}</span>
                      {r.igst > 0 ? (
                        <Badge className="ml-2 bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">Inter-state</Badge>
                      ) : (
                        <Badge variant="outline" className="ml-2 border-slate-300 text-slate-500 text-[10px]">Intra-state</Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-slate-600" data-testid={`gstr2-type-${r.id}`}>
                      {r.invoice_type || "—"}{r.tax_code ? <span className="ml-1 text-[10px] text-slate-400">{r.tax_code}</span> : null}
                      {r.tax_label ? <span className="block text-[10px] text-slate-500" data-testid={`gstr2-tax-label-${r.id}`}>{r.tax_label} <CategoryChip cat={r.tax_category} flag={r.tax_flag} /></span> : null}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{r.gst_rate}%</TableCell>
                    <TableCell className="text-sm text-slate-700 text-right">{inr(r.taxable_value)}</TableCell>
                    <TableCell className="text-sm text-slate-600 text-right">{r.igst ? inr(r.igst) : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 text-right">{r.cgst ? inr(r.cgst) : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 text-right">{r.sgst ? inr(r.sgst) : "—"}</TableCell>
                    <TableCell className="text-sm font-semibold text-slate-900 text-right">{inr(r.invoice_value)}</TableCell>
                    <TableCell>
                      <Badge className={r.itc_eligible === "Yes" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : r.itc_eligible === "RCM" ? "bg-sky-100 text-sky-700 hover:bg-sky-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                        {r.itc_eligible}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {data.total_rows > PAGE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-600" data-testid="gstr2-pager">
              <span>Showing {data.offset + 1}–{Math.min(data.offset + PAGE, data.total_rows)} of {data.total_rows.toLocaleString("en-IN")} lines{loading ? " · loading…" : ""} · summaries above cover all lines</span>
              <div className="flex gap-2">
                <button type="button" disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)} data-testid="gstr2-prev" className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-40 hover:bg-slate-50">Previous</button>
                <button type="button" disabled={data.offset + PAGE >= data.total_rows || loading} onClick={() => setPage((p) => p + 1)} data-testid="gstr2-next" className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-40 hover:bg-slate-50">Next</button>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400">{s.count} invoices · click any line to open its SAP accounting document</p>
        </CardContent>
      </Card>
    </div>
  );
}
